import { useState, useEffect, useMemo } from 'react';
import '@/assets/styles/components/left-menu.scss';
import CoachLogo from '@/assets/svg/coach-logo-with-text.svg';
import SolverLogo from '@/assets/svg/solver-logo-with-text.svg';
import GotItItLogo from '@/assets/svg/got-it-logo-with-text.svg';
import BoutiqueLogo from '@/assets/svg/boutique-logo-with-text.svg';
import { APPS } from '@/constants';
import { useNavigate } from 'react-router';
import { NavigationPath } from '@/router/navigation-path.ts';
import { useLocation } from 'react-router';
import { useApi } from '@/api/useApi.tsx';
import { useBoutiqueMenu, useQueryProvider } from '@/hooks';
import { BOUTIQUE_GROUP_NAME, BOUTIQUE_GROUP_TITLE, BoutiqueMenuItem } from '@/hooks/use-boutique-menu';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { useFeatureFlag } from 'configcat-react';
import { useAuthStore } from '@/zustand/auth/store.ts';
import { parseJwt, jsonTryParse } from '@/utility';
import { CONFIGCAT_KEYS } from '@/constants/configcat-keys.ts';
import clsx from 'clsx';

interface SubMenuItem {
	name: string;
	description: string;
	notificationCount?: number;
}

interface MenuGroup {
	liveServiceGroupName: string;
	subMenuItems: SubMenuItem[];
}

interface ServiceGroupInfo {
	id: string;
	title: string;
	icon: React.ReactNode;
}

interface SubMenuInfo {
	icon: string;
	navigationPath: string;
	exact?: boolean;
}

const SERVICE_GROUP_MAPPING: Record<string, ServiceGroupInfo> = {
	COACHING: {
		id: APPS.COACH,
		title: APPS.COACH,
		icon: (
			<img
				src={CoachLogo}
				className="coach-logo"
			/>
		)
	},
	GOT_IT: {
		id: APPS.GOT_IT,
		title: APPS.GOT_IT,
		icon: (
			<img
				src={GotItItLogo}
				className="gotit-logo"
			/>
		)
	},
	SOLVER: {
		id: APPS.SOLVER,
		title: APPS.SOLVER,
		icon: (
			<img
				src={SolverLogo}
				className="solver-logo"
			/>
		)
	},
	[BOUTIQUE_GROUP_NAME]: {
		id: BOUTIQUE_GROUP_NAME,
		title: BOUTIQUE_GROUP_TITLE,
		icon: (
			<img
				src={BoutiqueLogo}
				className="boutique-logo"
			/>
		)
	}
};

const SUB_MENU_MAPPING: Record<string, SubMenuInfo> = {
	[BoutiqueMenuItem.CALENDAR]: {
		icon: 'icons-calendar',
		navigationPath: NavigationPath.boutiqueClassroom,
		exact: true
	},
	[BoutiqueMenuItem.MY_STUDENTS]: {
		icon: 'icons-users-group-rounded',
		navigationPath: NavigationPath.boutiqueStudents
	},
	MY_APPOINTMENTS: {
		icon: 'icons-user-card',
		navigationPath: NavigationPath.coachAppointments
	},
	INSTANT_MEETINGS: {
		icon: 'icons-videocamera-record',
		navigationPath: NavigationPath.coachInstantMeetings
	},
	MY_STUDENTS: {
		icon: 'icons-users-group-rounded',
		navigationPath: NavigationPath.coachStudents
	},
	GOT_IT_MY_STUDENTS: {
		icon: 'icons-users-group-rounded',
		navigationPath: NavigationPath.gotItStudents
	},
	PAST_MEETINGS: {
		icon: 'icons-chat-round-video',
		navigationPath: NavigationPath.coachPastMeetings
	},
	GOT_IT_MY_APPOINTMENTS: {
		icon: 'icons-user-card',
		navigationPath: NavigationPath.gotItAppointments
	},
	GOT_IT_INSTANT_MEETINGS: {
		icon: 'icons-videocamera-record',
		navigationPath: NavigationPath.gotItInstantMeetings
	},
	GOT_IT_PAST_MEETINGS: {
		icon: 'icons-chat-round-video',
		navigationPath: NavigationPath.gotItPastMeetings
	},
	GOT_IT_PLANNED_PAST_MEETINGS: {
		icon: 'icons-chat-round-video',
		navigationPath: NavigationPath.gotItPlannedPastMeetings
	},
	GOT_IT_PLANNED_MY_APPOINTMENTS: {
		icon: 'icons-user-card',
		navigationPath: NavigationPath.gotItPlannedAppointments
	},
	EXPERT_REVIEW: {
		icon: 'icons-star',
		navigationPath: NavigationPath.solverExpertReview
	},
	SOLVE_QUESTION: {
		icon: 'icons-magic-stick',
		navigationPath: NavigationPath.solverSolve
	},
	ANSWERED_QUESTIONS: {
		icon: 'icons-clipboard-check',
		navigationPath: NavigationPath.solverSolvedQuestions
	},
	RESOLVE_QUESTIONS: {
		icon: 'icons-undo-left-arrow',
		navigationPath: NavigationPath.solverResolveQuestions
	}
};

export const LeftMenu = () => {
	const { MenuItemControllerApi, SupportDocumentControllerApi } = useApi();
	const [openGroups, setOpenGroups] = useState<string[]>([]);
	const navigate = useNavigate();
	const location = useLocation();
	const { data: authData } = useAuthStore();
	const { hasBoutiqueAccess, boutiqueMenuGroup } = useBoutiqueMenu();
	const { value: reviewerExpertIdListRaw } = useFeatureFlag(CONFIGCAT_KEYS.REVIEWER_EXPERT_ID_LIST, '[]');
	const { value: assignToMeExpertIdListRaw } = useFeatureFlag(CONFIGCAT_KEYS.ASSIGN_TO_ME_EXPERT_ID_LIST, '[]');

	const isReviewer = useMemo(() => {
		const expertId = authData?.access_token ? parseJwt(authData.access_token)?.additional_info?.expert_id : null;
		if (!expertId) return false;
		return jsonTryParse(reviewerExpertIdListRaw as string).some((id: number) => id === parseInt(String(expertId)));
	}, [authData?.access_token, reviewerExpertIdListRaw]);

	const isActiveAssignToMe = useMemo(() => {
		const expertId = authData?.access_token ? parseJwt(authData.access_token)?.additional_info?.expert_id : null;
		if (!expertId) return false;
		return jsonTryParse(assignToMeExpertIdListRaw as string).some((id: number) => id === parseInt(String(expertId)));
	}, [authData?.access_token, assignToMeExpertIdListRaw]);

	const { data: apiResponse } = useQueryProvider([QUERY_KEYS.getAllMenuItems], () => MenuItemControllerApi.getAllMenuItems(), {
		select: ({ data }) => data,
		refetchOnWindowFocus: false
	});

	const { data: supportDocTotal } = useQueryProvider(
		[QUERY_KEYS.findSupportDocuments, 'menu-check'],
		() => SupportDocumentControllerApi.findSupportDocuments(undefined, undefined, 0, 1),
		{
			select: ({ data }) => data?.data?.totalElements ?? 0,
			refetchOnWindowFocus: false
		}
	);

	const hasSupportDocs = (supportDocTotal ?? 0) > 0;

	const menuItems: MenuGroup[] = useMemo(
		() => [...(hasBoutiqueAccess ? [boutiqueMenuGroup] : []), ...((apiResponse?.data as MenuGroup[]) ?? [])],
		[apiResponse, boutiqueMenuGroup, hasBoutiqueAccess]
	);

	useEffect(() => {
		if (menuItems.length > 0) {
			setOpenGroups(menuItems.map(g => g.liveServiceGroupName));
		}
	}, [menuItems]);

	const toggleGroup = (groupId: string) => {
		setOpenGroups(prev => (prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]));
	};

	const onNavigate = (navigationPath: string) => {
		navigate(navigationPath);
	};

	const isPathActive = (menuPath: string, currentPath: string): boolean => {
		if (menuPath === currentPath) return true;

		return currentPath.startsWith(menuPath + '/');
	};

	return (
		<div className="sidebar">
			{hasSupportDocs && (
				<div
					className={clsx('sidebar__menu-item sidebar__support-item', { '--active': isPathActive(NavigationPath.supportDocuments, location.pathname) })}
					onClick={() => onNavigate(NavigationPath.supportDocuments)}>
					<i className="icons-document" />
					<span className="sibebar__menu--title">Destek Dokümanları</span>
				</div>
			)}
			{menuItems?.map((group: MenuGroup) => {
				const serviceGroupInfo = SERVICE_GROUP_MAPPING[group.liveServiceGroupName];

				if (!serviceGroupInfo) {
					return null;
				}

				const rawItems = group.subMenuItems || [];
				let subMenuItems = rawItems;
				if (group.liveServiceGroupName === 'SOLVER') {
					if (isReviewer && !subMenuItems.some(i => i.name === 'EXPERT_REVIEW')) {
						subMenuItems = [{ name: 'EXPERT_REVIEW', description: 'Uzman Değerlendirme' }, ...subMenuItems];
					}
					if (isActiveAssignToMe && !subMenuItems.some(i => i.name === 'RESOLVE_QUESTIONS')) {
						const expertReviewIndex = subMenuItems.findIndex(i => i.name === 'EXPERT_REVIEW');
						const insertAt = expertReviewIndex >= 0 ? expertReviewIndex + 1 : 0;
						subMenuItems = [
							...subMenuItems.slice(0, insertAt),
							{ name: 'RESOLVE_QUESTIONS', description: 'Yeniden Atanan Sorular' },
							...subMenuItems.slice(insertAt)
						];
					}
				}

				return (
					<div
						key={group.liveServiceGroupName}
						className="sidebar__menu-group">
						<div
							className="sidebar__menu-header"
							onClick={() => toggleGroup(group.liveServiceGroupName)}>
							<div className="sidebar__menu-title">
								<div className="sidebar__menu-icon">{serviceGroupInfo.icon}</div>
							</div>
							<i className={openGroups.includes(group.liveServiceGroupName) ? 'sidebar__menu-arrow icons-alt-arrow-up' : 'sidebar__menu-arrow icons-alt-arrow-down'} />
						</div>
						<div className={openGroups.includes(group.liveServiceGroupName) ? 'sidebar__menu-content' : 'sidebar__menu-content sidebar__menu-content--closed'}>
							{subMenuItems.map((item: SubMenuItem) => {
								const subMenuInfo = SUB_MENU_MAPPING?.[item.name];

								if (!subMenuInfo) {
									return null;
								}

								const isActive = subMenuInfo.exact ? location.pathname === subMenuInfo.navigationPath : isPathActive(subMenuInfo.navigationPath, location.pathname);

								return (
									<div
										key={item.name}
										className={isActive ? 'sidebar__menu-item --active' : 'sidebar__menu-item'}
										onClick={() => onNavigate(subMenuInfo.navigationPath)}>
										{subMenuInfo.icon && <i className={subMenuInfo.icon} />}
										<span className="sibebar__menu--title">{item.description}</span>
										{item.notificationCount && item.notificationCount > 0 && <span className="sidebar__menu-item--notification-count">{item.notificationCount}</span>}
									</div>
								);
							})}
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default LeftMenu;
